import type { MouseEvent, ReactNode, RefObject } from "react";
import { Menu } from "@headlessui/react";
import { ClipLoader } from "react-spinners";
import {
  FiDownload,
  FiEye,
  FiFolder,
  FiMoreHorizontal,
  FiMoreVertical,
} from "react-icons/fi";
import { FaLocationCrosshairs } from "react-icons/fa6";
import { MdFileUploadOff } from "react-icons/md";
import prettyBytes from "pretty-bytes";
import { formatDateInUtcTimeZone } from "../../utils";
import { FileSystemItem } from "./types";
import { getChildCount, getBrowseItemIcon, getRelativeLocation } from "./utils";

type ItemsByYear = { year: string; items: FileSystemItem[] };

type BrowseItemsProps = {
  viewMode: "grid" | "list";
  itemsByYear: ItemsByYear[];
  selectedItems: Set<string>;
  onItemSelection: (itemPath: string, event: MouseEvent) => void;
  onItemClick: (item: FileSystemItem) => void;
  onPreview: (item: FileSystemItem) => void;
  onSeeDeployment: (item: FileSystemItem) => void;
  getPreviewMeta: (item: FileSystemItem) => {
    assetType: string;
    canPreview: boolean;
  };
  getDownloadUrl: (item: FileSystemItem) => string | null;
  hasDeploymentFeature: boolean;
  isOpeningDeployment: boolean;
  currentPath: string[];
  searchQuery: string;
  debouncedSearchQuery: string;
  hasActiveFilters: () => boolean;
  hoveredIcon: string | null;
  setHoveredIcon: (value: string | null) => void;
  onClearSelection: () => void;
  isFetchingNodes: boolean;
  isLoadingMore: boolean;
  loadMoreSentinelRef: RefObject<HTMLDivElement>;
  lastOpenedItemKey: string | null;
};

const getItemKey = (item: FileSystemItem) =>
  item.dataAssetPublicId || item.path;

const LastViewedBadge = () => (
  <span className="shrink-0 rounded bg-orange px-2 py-0.5 text-xs font-semibold text-blue-500">
    Last Viewed
  </span>
);

type BrowseFileActionsMenuProps = {
  item: FileSystemItem;
  triggerIcon: ReactNode;
  canPreviewFile: boolean;
  canSeeDeployment: boolean;
  canDownload: boolean;
  downloadUrl: string | null;
  isOpeningDeployment: boolean;
  onPreview: (item: FileSystemItem) => void;
  onSeeDeployment: (item: FileSystemItem) => void;
  className?: string;
  menuItemsClassName?: string;
};

const BrowseFileActionsMenu = ({
  item,
  triggerIcon,
  canPreviewFile,
  canSeeDeployment,
  canDownload,
  downloadUrl,
  isOpeningDeployment,
  onPreview,
  onSeeDeployment,
  className = "relative",
  menuItemsClassName = "absolute right-0 mt-2 w-48 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50",
}: BrowseFileActionsMenuProps) => (
  <Menu as="div" className={className}>
    <Menu.Button
      aria-label="Menu"
      className="text-gray-500 hover:text-gray-700 focus:outline-none"
      onClick={(e) => e.stopPropagation()}
    >
      {triggerIcon}
    </Menu.Button>
    <Menu.Items className={menuItemsClassName}>
      <Menu.Item>
        {({ active }) => (
          <button
            aria-label="Preview file"
            className={`${
              active ? "bg-gray-100" : ""
            } group flex w-full items-center px-3 py-2 text-sm ${
              canPreviewFile
                ? "text-gray-700"
                : "cursor-not-allowed text-gray-400"
            }`}
            disabled={!canPreviewFile}
            onClick={(e) => {
              e.stopPropagation();
              if (canPreviewFile) {
                onPreview(item);
              }
            }}
          >
            <FiEye className="mr-2" size={16} />
            Preview
          </button>
        )}
      </Menu.Item>
      <Menu.Item>
        {({ active }) => (
          <button
            aria-label="See Deployment"
            className={`${
              active ? "bg-gray-100" : ""
            } group flex w-full items-center px-3 py-2 text-sm ${
              canSeeDeployment
                ? "text-gray-700"
                : "cursor-not-allowed text-gray-400"
            }`}
            disabled={!canSeeDeployment || isOpeningDeployment}
            onClick={(e) => {
              e.stopPropagation();
              if (canSeeDeployment) {
                onSeeDeployment(item);
              }
            }}
          >
            <FaLocationCrosshairs className="mr-2" size={16} />
            See Deployment
          </button>
        )}
      </Menu.Item>
      <Menu.Item>
        {({ active }) =>
          canDownload ? (
            <a
              href={downloadUrl ?? undefined}
              aria-label="Download file"
              className={`${
                active ? "bg-gray-100" : ""
              } group flex w-full items-center px-3 py-2 text-sm text-gray-700`}
              onClick={(e) => e.stopPropagation()}
            >
              <FiDownload className="mr-2" size={16} />
              Download
            </a>
          ) : (
            <button
              aria-label="Download file"
              className={`${
                active ? "bg-gray-100" : ""
              } group flex w-full cursor-not-allowed items-center px-3 py-2 text-sm text-gray-400`}
              disabled
            >
              <FiDownload className="mr-2" size={16} />
              Download
            </button>
          )
        }
      </Menu.Item>
    </Menu.Items>
  </Menu>
);

const BrowseItems = ({
  viewMode,
  itemsByYear,
  selectedItems,
  onItemSelection,
  onItemClick,
  onPreview,
  onSeeDeployment,
  getPreviewMeta,
  getDownloadUrl,
  hasDeploymentFeature,
  isOpeningDeployment,
  currentPath,
  searchQuery,
  debouncedSearchQuery,
  hasActiveFilters,
  hoveredIcon,
  setHoveredIcon,
  onClearSelection,
  isFetchingNodes,
  isLoadingMore,
  loadMoreSentinelRef,
  lastOpenedItemKey,
}: BrowseItemsProps) => {
  const showLocation = !!(debouncedSearchQuery || hasActiveFilters());
  const isSearchDebouncing = searchQuery !== debouncedSearchQuery;
  const showInitialLoading =
    isSearchDebouncing || (isFetchingNodes && !isLoadingMore);

  return (
    <>
      {showInitialLoading ? (
        <div className="flex justify-center py-8">
          <ClipLoader color="#3B82F6" size={35} />
        </div>
      ) : viewMode === "grid" ? (
        <div className="mt-4">
          {itemsByYear.map(({ year, items }, yearIndex) => (
            <div
              key={year || "all"}
              className={yearIndex > 0 && year ? "mt-8" : ""}
            >
              {year && (
                <div className="mb-4">
                  <h3 className="mb-2 text-lg font-medium text-gray-500">
                    {year}
                  </h3>
                  <hr className="border-gray-300" />
                </div>
              )}
              <div
                className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    onClearSelection();
                  }
                }}
              >
                {items.map((item) => {
                  const isJustOpened = lastOpenedItemKey === getItemKey(item);
                  const previewMetaForItem = getPreviewMeta(item);
                  const canPreviewFile =
                    previewMetaForItem.canPreview && !!item.dataAssetPublicId;
                  const downloadUrl = getDownloadUrl(item);
                  const canDownload = !!downloadUrl;
                  const canSeeDeployment = !!(
                    item.realAssetDeploymentPublicId && hasDeploymentFeature
                  );
                  const childCount = getChildCount(item);
                  const childCountLabel =
                    currentPath.length > 1 && childCount && childCount > 0
                      ? `${childCount}`
                      : undefined;

                  return (
                    <div
                      key={item.path}
                      className={`relative cursor-pointer select-none rounded-lg border p-4 transition-all ${
                        selectedItems.has(item.path)
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      }`}
                      onClick={(e) => {
                        onItemSelection(item.path, e);
                        if (
                          !e.ctrlKey &&
                          !e.metaKey &&
                          !e.shiftKey &&
                          item.type === "folder"
                        ) {
                          onItemClick(item);
                        }
                      }}
                      onDoubleClick={() =>
                        item.type === "folder"
                          ? onItemClick(item)
                          : onPreview(item)
                      }
                    >
                      {item.type === "file" && (
                        <BrowseFileActionsMenu
                          item={item}
                          triggerIcon={<FiMoreVertical size={16} />}
                          canPreviewFile={canPreviewFile}
                          canSeeDeployment={canSeeDeployment}
                          canDownload={canDownload}
                          downloadUrl={downloadUrl}
                          isOpeningDeployment={isOpeningDeployment}
                          onPreview={onPreview}
                          onSeeDeployment={onSeeDeployment}
                          className="absolute right-2 top-2 z-10"
                        />
                      )}
                      <div
                        className={`mb-2 flex min-w-0 items-center ${
                          item.type === "file" ? "pr-8" : ""
                        }`}
                      >
                        <div className="mr-2 flex-shrink-0">
                          {getBrowseItemIcon(item, {
                            folderSize: 24,
                            folderClassName: "flex-shrink-0 text-yellow-500",
                          })}
                        </div>
                        <div className="min-w-0 flex-1">
                          {item.cruiseId && item.displayName ? (
                            <div>
                              <div className="flex min-w-0 items-center gap-2">
                                <span
                                  className="block min-w-0 flex-1 truncate text-sm font-semibold"
                                  title={item.cruiseId}
                                >
                                  {item.cruiseId}
                                </span>
                                {isJustOpened && <LastViewedBadge />}
                                {item.noUpload && (
                                  <div className="relative">
                                    <MdFileUploadOff
                                      size={14}
                                      className="text-orange-500 flex-shrink-0 opacity-70"
                                      onMouseEnter={() =>
                                        setHoveredIcon(
                                          `upload-grid-${item.path}`
                                        )
                                      }
                                      onMouseLeave={() => setHoveredIcon(null)}
                                    />
                                    {hoveredIcon ===
                                      `upload-grid-${item.path}` && (
                                      <div className="absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2 transform whitespace-nowrap rounded border border-gray-600 bg-gray-800 px-2 py-1 text-xs text-gray-100">
                                        Uploads disabled for this folder
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              <span
                                className="block truncate text-xs text-gray-500"
                                title={item.displayName}
                              >
                                {item.displayName}
                              </span>
                            </div>
                          ) : (
                            <div className="flex min-w-0 items-center gap-2">
                              <span
                                className="block min-w-0 flex-1 truncate text-sm font-medium"
                                title={item.name}
                              >
                                {item.name}
                              </span>
                              {isJustOpened && <LastViewedBadge />}
                              {item.noUpload && (
                                <div className="relative">
                                  <MdFileUploadOff
                                    size={14}
                                    className="text-orange-500 flex-shrink-0 opacity-70"
                                    onMouseEnter={() =>
                                      setHoveredIcon(`upload-grid-${item.path}`)
                                    }
                                    onMouseLeave={() => setHoveredIcon(null)}
                                  />
                                  {hoveredIcon ===
                                    `upload-grid-${item.path}` && (
                                    <div className="absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2 transform whitespace-nowrap rounded border border-gray-600 bg-gray-800 px-2 py-1 text-xs text-gray-100">
                                      Uploads disabled for this folder
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-end justify-between">
                        <div className="min-w-0 flex-1">
                          {showLocation && (
                            <div
                              className="mb-1 truncate text-xs text-gray-500"
                              title={getRelativeLocation(
                                item.path,
                                currentPath
                              )}
                            >
                              {getRelativeLocation(item.path, currentPath)}
                            </div>
                          )}
                          {item.type === "file" && item.size && (
                            <div className="text-xs text-gray-600">
                              <div>Size: {prettyBytes(item.size)}</div>
                              {item.modified && (
                                <div>
                                  Modified:{" "}
                                  {formatDateInUtcTimeZone(item.modified)}
                                </div>
                              )}
                            </div>
                          )}
                          {item.type === "folder" && (
                            <div className="text-xs text-gray-600">
                              {childCountLabel
                                ? `${childCountLabel} items`
                                : ""}
                            </div>
                          )}
                        </div>
                        {item.type === "file" && item.modifiedBy && (
                          <div className="group relative ml-2">
                            <div
                              className={`flex h-6 w-6 items-center justify-center rounded-full ${
                                item.modifiedBy.color || "bg-blue-500"
                              } cursor-pointer text-xs font-semibold text-white`}
                            >
                              {item.modifiedBy.initials}
                            </div>
                            <div className="pointer-events-none absolute bottom-full right-0 z-50 mb-2 whitespace-nowrap rounded border border-gray-600 bg-gray-800 px-2 py-1 text-xs text-gray-100 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                              Last modified: {item.modifiedBy.name}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-4">
          <div className="sticky top-0 z-10 flex items-center border-b border-gray-300 bg-zinc-50 px-4 py-2 text-xs font-semibold text-gray-600">
            <div className="w-5" />
            <span className="w-96">Name</span>
            <div className="flex flex-1 justify-end pr-8">
              {showLocation && <span>Location</span>}
            </div>
            <span className="w-20 text-right">Size</span>
            <span className="w-24 text-right">Uploaded</span>
            <span className="w-24 text-right">Modified</span>
            <span className="w-24 text-center">Modified By</span>
            <span className="w-8" />
          </div>
          {itemsByYear.map(({ year, items }, yearIndex) => (
            <div key={year || "all"}>
              {year && (
                <div
                  className={`flex items-center border-y border-gray-300 bg-gray-100 px-4 py-2 ${
                    yearIndex > 0 ? "mt-2" : ""
                  }`}
                >
                  <span className="text-sm font-medium text-gray-600">
                    {year}
                  </span>
                </div>
              )}
              <div className="space-y-1">
                {items.map((item) => {
                  const isJustOpened = lastOpenedItemKey === getItemKey(item);
                  const previewMetaForItem = getPreviewMeta(item);
                  const canPreviewFile =
                    previewMetaForItem.canPreview && !!item.dataAssetPublicId;
                  const downloadUrl = getDownloadUrl(item);
                  const canDownload = !!downloadUrl;
                  const canSeeDeployment = !!(
                    item.realAssetDeploymentPublicId && hasDeploymentFeature
                  );
                  const childCount = getChildCount(item);
                  const childCountLabel =
                    currentPath.length > 1 && childCount && childCount > 0
                      ? `${childCount}`
                      : undefined;

                  return (
                    <div
                      key={item.path}
                      className={`flex cursor-pointer select-none items-center rounded border-l-2 px-4 py-2 transition-all ${
                        selectedItems.has(item.path)
                          ? "border-blue-500 bg-blue-50"
                          : "border-transparent hover:bg-gray-50"
                      }`}
                      onClick={(e) => {
                        onItemSelection(item.path, e);
                        if (
                          !e.ctrlKey &&
                          !e.metaKey &&
                          !e.shiftKey &&
                          item.type === "folder"
                        ) {
                          onItemClick(item);
                        }
                      }}
                      onDoubleClick={() =>
                        item.type === "folder"
                          ? onItemClick(item)
                          : onPreview(item)
                      }
                    >
                      <div className="flex-shrink-0">
                        {getBrowseItemIcon(item, {
                          folderSize: 20,
                          folderClassName: "flex-shrink-0 text-yellow-500",
                        })}
                      </div>
                      <div className="flex w-96 min-w-0 items-center gap-2 pr-4">
                        {item.cruiseId && item.displayName ? (
                          <div
                            className="flex min-w-0 items-center gap-2"
                            title={`${item.cruiseId} — ${item.displayName}`}
                          >
                            <span className="truncate text-sm">
                              {item.cruiseId}
                            </span>
                            {isJustOpened && <LastViewedBadge />}
                            <span className="truncate text-xs text-gray-500">
                              {item.displayName}
                            </span>
                          </div>
                        ) : (
                          <>
                            <span
                              className="truncate text-sm"
                              title={item.name}
                            >
                              {item.name}
                            </span>
                            {isJustOpened && <LastViewedBadge />}
                          </>
                        )}
                        {item.type === "folder" && item.noUpload && (
                          <div className="relative">
                            <MdFileUploadOff
                              size={14}
                              className="text-orange-500 opacity-70"
                              onMouseEnter={() =>
                                setHoveredIcon(`upload-list-${item.path}`)
                              }
                              onMouseLeave={() => setHoveredIcon(null)}
                            />
                            {hoveredIcon === `upload-list-${item.path}` && (
                              <div className="absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2 transform whitespace-nowrap rounded border border-gray-600 bg-gray-800 px-2 py-1 text-xs text-gray-100">
                                Uploads disabled for this folder
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1 pr-8">
                        {showLocation && (
                          <span
                            className="block truncate text-xs text-gray-600"
                            title={getRelativeLocation(item.path, currentPath)}
                          >
                            {getRelativeLocation(item.path, currentPath)}
                          </span>
                        )}
                      </div>
                      <span className="w-20 text-right text-xs text-gray-600">
                        {item.type === "file" && item.size
                          ? prettyBytes(item.size)
                          : item.type === "folder" && childCountLabel
                          ? `${childCountLabel} items`
                          : "-"}
                      </span>
                      <span className="w-24 text-right text-xs text-gray-600">
                        {item.uploaded
                          ? formatDateInUtcTimeZone(item.uploaded)
                          : "-"}
                      </span>
                      <span className="w-24 text-right text-xs text-gray-600">
                        {item.modified
                          ? formatDateInUtcTimeZone(item.modified)
                          : "-"}
                      </span>
                      <div className="flex w-24 justify-center">
                        {item.type === "file" && item.modifiedBy ? (
                          <div className="group relative">
                            <div
                              className={`flex h-6 w-6 items-center justify-center rounded-full ${
                                item.modifiedBy.color || "bg-blue-500"
                              } cursor-pointer text-xs font-semibold text-white`}
                            >
                              {item.modifiedBy.initials}
                            </div>
                            <div className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2 transform whitespace-nowrap rounded border border-gray-600 bg-gray-800 px-2 py-1 text-xs text-gray-100 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                              {item.modifiedBy.name}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-600">-</span>
                        )}
                      </div>
                      <div className="flex w-8 justify-center">
                        {item.type === "file" && (
                          <BrowseFileActionsMenu
                            item={item}
                            triggerIcon={<FiMoreHorizontal size={16} />}
                            canPreviewFile={canPreviewFile}
                            canSeeDeployment={canSeeDeployment}
                            canDownload={canDownload}
                            downloadUrl={downloadUrl}
                            isOpeningDeployment={isOpeningDeployment}
                            onPreview={onPreview}
                            onSeeDeployment={onSeeDeployment}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {!showInitialLoading &&
        (itemsByYear.length === 0 ||
          (itemsByYear.length === 1 && itemsByYear[0].items.length === 0)) && (
          <div className="mt-8 text-center text-gray-600">
            <FiFolder size={48} className="mx-auto mb-2 opacity-50" />
            <p>
              {showLocation
                ? "No items match your search"
                : "This folder is empty"}
            </p>
          </div>
        )}

      {isLoadingMore && (
        <div className="flex justify-center py-6">
          <ClipLoader color="#3B82F6" size={28} />
        </div>
      )}
      <div ref={loadMoreSentinelRef} className="h-1" />
    </>
  );
};

export default BrowseItems;
